import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

/**
 * How a parent's allbreed slug was resolved. Shared by the AllBreed queue page
 * and the per-horse submit dialog so the wording stays identical in both.
 */
export const SOURCE_LABEL = {
  own: 'Already on allbreed',
  local: 'Matched locally',
  network: 'Found on allbreed',
};

/**
 * The identifying facts for one allbreed candidate, on two lines: what the
 * horse is, then who its parents are. Parentage is usually what separates
 * same-named horses, so it gets its own line rather than being buried in a
 * single run-on string. Shared with the duplicate warning in the submit dialog.
 *
 * Sire and dam come only from the network tier — the local horse_ancestors
 * lookup has no parent columns — so the second line is omitted when unknown.
 */
export function CandidateDetail({ candidate }) {
  const facts = [candidate.breed, candidate.sex, candidate.color, candidate.year, candidate.reg_number]
    .filter(Boolean)
    .join(' · ');
  const parents = [
    candidate.sire && `Sire: ${candidate.sire}`,
    candidate.dam && `Dam: ${candidate.dam}`,
  ].filter(Boolean);

  return (
    <>
      {facts && (
        <Typography variant="body2" color="text.secondary" component="div">
          {facts}
        </Typography>
      )}
      {parents.length > 0 && (
        <Typography variant="body2" color="text.secondary" component="div">
          {parents.join(' · ')}
        </Typography>
      )}
    </>
  );
}

/**
 * Picker shown when a parent name matches more than one horse on allbreed.
 * Calls onSelect(candidate) with the chosen row.
 */
function AllBreedCandidateDialog({ open, title, candidates = [], onSelect, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {candidates.length === 0 ? (
          <Typography color="text.secondary">No candidates found.</Typography>
        ) : (
          <List disablePadding>
            {candidates.map((c, i) => (
              <React.Fragment key={c.slug}>
                {i > 0 && <Divider />}
                <ListItemButton onClick={() => onSelect(c)} alignItems="flex-start">
                  <ListItemText
                    primary={c.name}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={<CandidateDetail candidate={c} />}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1, whiteSpace: 'nowrap' }}
                  >
                    {c.slug}
                  </Typography>
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AllBreedCandidateDialog;
